import { type ReactNode, type RefObject, useId, useRef, useState } from "react";
import { CollisionPriority } from "@dnd-kit/abstract";
import { pointerIntersection } from "@dnd-kit/collision";
import type { Section } from "@patent-studio/api-types";
import { BookA } from "lucide-react";
import { useSortableItem, useTarget } from "@/components/patterns/SortableList";
import { Button } from "@/components/ui/button";
import { DisclosureToggle, useDisclosure } from "@/components/ui/Disclosure";
import { EmptyState } from "@/components/ui/EmptyState";
import { IconTile } from "@/components/ui/IconTile";
import { Panel } from "@/components/ui/Panel";
import { useLane } from "@/components/ui/SortableLanes";
import { mergeRefs } from "@/lib/merge-refs";
import { plural } from "@/lib/plural";
import { cn } from "@/lib/utils";
import { AddField } from "./AddField";
import { DeleteSectionPanel } from "./DeleteSectionPanel";
import { intoSection } from "./FrameworkDrag";
import { SECTIONS, laneOf } from "./framework-edits";
import { InlineLine } from "./InlineLine";
import type { DefinitionWords, LadderKey } from "./framework-edits";
import type { LadderActions } from "./RangeLadders";
import {
  type DefinitionActions,
  type Labels,
  type Subsection,
  SubsectionRow,
} from "./SubsectionRow";
import { TEXT_LIMITS } from "@/lib/text-limits";

export interface SectionActions {
  rename: (title: string, putBack: () => void) => void;
  reword: (subsectionId: string, text: string, putBack: () => void) => void;
  /** A Definitions subsection's lines, added, reworded or dropped one at a time. */
  definitions: {
    add: (
      subsectionId: string,
      words: DefinitionWords,
      putBack: () => void,
    ) => void;
    reword: (
      subsectionId: string,
      itemId: string,
      words: DefinitionWords,
      putBack: () => void,
    ) => void;
    drop: (subsectionId: string, itemId: string) => void;
  };
  /** A claim row's range ladders, each rung accepted, reworded, added or dropped on its own. */
  ladders: {
    accept: (
      subsectionId: string,
      ladder: LadderKey,
      itemId: string,
      accepted: boolean,
    ) => void;
    reword: (
      subsectionId: string,
      ladder: LadderKey,
      itemId: string,
      key: string,
      putBack: () => void,
    ) => void;
    add: (
      subsectionId: string,
      ladder: LadderKey,
      key: string,
      putBack: () => void,
    ) => void;
    drop: (subsectionId: string, ladder: LadderKey, itemId: string) => void;
  };
  deleteSubsection: (subsection: Subsection) => void;
  deleteSection: (moveTo?: string) => void;
  addLine: (text: string, putBack: () => void) => void;
  /** Opens the section with an empty Definitions subsection; offered on the first section while the framework holds none. */
  addDefinitions?: () => void;
}

/** Whether a card is open, held by the page so its "Collapse all" can read every card. */
export interface Fold {
  open: boolean;
  setOpen: (open: boolean) => void;
}

/**
 * Which cards are open, held by the page: "Collapse all" while any card is open, else "Expand
 * all", as v0.26 reads its cards. A card added later starts open.
 */
export function useFolds(sectionIds: string[]) {
  const [shut, setShut] = useState<ReadonlySet<string>>(() => new Set());
  const anyOpen = sectionIds.some((id) => !shut.has(id));
  return {
    anyOpen,
    foldAll: () => {
      setShut(new Set(anyOpen ? sectionIds : []));
    },
    of: (sectionId: string): Fold => ({
      open: !shut.has(sectionId),
      setOpen: (open) => {
        setShut((was) => {
          const next = new Set(was);
          if (open) next.delete(sectionId);
          else next.add(sectionId);
          return next;
        });
      },
    }),
  };
}

interface CardProps {
  section: Section;
  /** The subsections in the order the page shows them, which a drag runs ahead of. */
  ordered: Subsection[];
  index: number;
  labels: Labels;
  orphans: ReadonlySet<string>;
  readOnly: boolean;
  others: { id: string; title: string }[];
  actions: SectionActions;
  folding: Fold;
}

interface Drag {
  ref: (element: Element | null) => void;
  handle: ReactNode;
  chrome: string;
  laneRef: (element: Element | null) => void;
}

/** The section's head row: handle, fold, number, title in place, what it holds when folded, delete. */
function SectionHead({
  title,
  number,
  fold,
  held,
  handle,
  readOnly,
  onRename,
  onDelete,
  deleteRef,
}: {
  title: string;
  number: number;
  fold: ReturnType<typeof useDisclosure>;
  held: string;
  handle: ReactNode;
  readOnly: boolean;
  onRename: (title: string, putBack: () => void) => void;
  onDelete: () => void;
  deleteRef: RefObject<HTMLButtonElement | null>;
}) {
  return (
    <div className="flex items-center gap-2">
      {handle}
      <DisclosureToggle trigger={fold.trigger} open={fold.open} names={title} />
      <IconTile size="xs" numeral={number} />
      <InlineLine
        value={title}
        label={`Section ${String(number)} title`}
        readOnly={readOnly}
        maxLength={TEXT_LIMITS.sectionTitle}
        onSave={onRename}
        className="min-w-0 flex-1 font-semibold"
      />
      {!fold.open && (
        <span className="text-xs text-muted-foreground">{held}</span>
      )}
      {!readOnly && (
        <Button
          ref={deleteRef}
          variant="danger-outline"
          size="dense"
          onClick={onDelete}
        >
          Delete section
        </Button>
      )}
    </div>
  );
}

function Subsections({
  section,
  ordered,
  labels,
  orphans,
  readOnly,
  actions,
  laneRef,
  onDeleted,
}: CardProps & {
  laneRef?: Drag["laneRef"];
  /** Called with the row's place as it is deleted, before it leaves. */
  onDeleted: (index: number) => void;
}) {
  const lane = laneOf(section.section_id);
  return (
    <div ref={laneRef}>
      {ordered.length === 0 ? (
        <EmptyState shape="row">
          No subsections. Drag one here or add a text line.
        </EmptyState>
      ) : (
        <ul className="space-y-1.5">
          {ordered.map((subsection, index) => (
            <SubsectionRow
              key={subsection.subsection_id}
              lane={lane}
              index={index}
              subsection={subsection}
              labels={labels}
              orphan={orphans.has(subsection.subsection_id)}
              readOnly={readOnly}
              onReword={(text, putBack) => {
                actions.reword(subsection.subsection_id, text, putBack);
              }}
              definitions={definitionActions(actions, subsection.subsection_id)}
              ladders={ladderActions(actions, subsection.subsection_id)}
              onDelete={() => {
                onDeleted(index);
                actions.deleteSubsection(subsection);
              }}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function ladderActions(
  actions: SectionActions,
  subsectionId: string,
): LadderActions {
  return {
    accept: (ladder, itemId, accepted) => {
      actions.ladders.accept(subsectionId, ladder, itemId, accepted);
    },
    reword: (ladder, itemId, key, putBack) => {
      actions.ladders.reword(subsectionId, ladder, itemId, key, putBack);
    },
    add: (ladder, key, putBack) => {
      actions.ladders.add(subsectionId, ladder, key, putBack);
    },
    drop: (ladder, itemId) => {
      actions.ladders.drop(subsectionId, ladder, itemId);
    },
  };
}

function definitionActions(
  actions: SectionActions,
  subsectionId: string,
): DefinitionActions {
  return {
    add: (words, putBack) => {
      actions.definitions.add(subsectionId, words, putBack);
    },
    reword: (itemId, words, putBack) => {
      actions.definitions.reword(subsectionId, itemId, words, putBack);
    },
    drop: (itemId) => {
      actions.definitions.drop(subsectionId, itemId);
    },
  };
}

/** What the card holds open: its rows, then the field that adds a line. */
function Body({
  cardRef,
  ...props
}: CardProps & {
  laneRef?: Drag["laneRef"];
  cardRef: RefObject<HTMLElement | null>;
}) {
  return (
    <>
      <Subsections
        {...props}
        onDeleted={(index) => {
          nextAfter(cardRef.current, index)?.focus();
        }}
      />
      {!props.readOnly && (
        <AddField
          placeholder="Add a subsection"
          action="Add"
          maxLength={TEXT_LIMITS.subsectionLine}
          onAdd={({ text }, putBack) => {
            props.actions.addLine(text, putBack);
          }}
        />
      )}
      {!props.readOnly && props.actions.addDefinitions !== undefined && (
        <Button
          variant="quiet"
          size="dense"
          className="mt-1.5"
          onClick={props.actions.addDefinitions}
        >
          <BookA aria-hidden />
          Add definitions
        </Button>
      )}
    </>
  );
}

/**
 * Where focus goes when the row at `index` is deleted: the next row's delete, else the one
 * before, else the field that adds a line, so a keyboard user stays where they were working.
 */
function nextAfter(
  card: HTMLElement | null,
  index: number,
): HTMLElement | null {
  if (card === null) return null;
  const deletes = Array.from(
    card.querySelectorAll<HTMLElement>("[data-subsection-delete]"),
  );
  return (
    deletes.at(index + 1) ??
    (index > 0 ? deletes.at(index - 1) : undefined) ??
    card.querySelector<HTMLElement>('input[aria-label="Add a subsection"]')
  );
}

function Card(props: CardProps & { drag?: Drag }) {
  const { section, ordered, index, others, readOnly, actions, drag } = props;
  const fold = useDisclosure(true, props.folding);
  const cardRef = useRef<HTMLElement>(null);
  const deleteRef = useRef<HTMLButtonElement>(null);
  const [asking, setAsking] = useState(false);
  const titleId = useId();
  const figures = ordered.filter((sub) => sub.kind === "figure").length;
  return (
    <Panel
      as="section"
      ref={mergeRefs(
        (element) => {
          cardRef.current = element instanceof HTMLElement ? element : null;
        },
        ...(drag === undefined ? [] : [drag.ref]),
      )}
      shape="card"
      pad="snug"
      role="group"
      aria-labelledby={titleId}
      className={drag?.chrome}
    >
      <span id={titleId} className="sr-only">
        {section.title}
      </span>
      <SectionHead
        title={section.title}
        number={index + 1}
        fold={fold}
        held={`${plural(ordered.length, "subsection")} · ${plural(figures, "figure")}`}
        handle={drag?.handle}
        readOnly={readOnly}
        onRename={actions.rename}
        deleteRef={deleteRef}
        onDelete={() => {
          if (ordered.length === 0) actions.deleteSection();
          else setAsking(true);
        }}
      />
      {asking && (
        <DeleteSectionPanel
          title={section.title}
          subsections={ordered.length}
          figures={figures}
          others={others}
          onMove={(target) => {
            setAsking(false);
            actions.deleteSection(target);
          }}
          onDeleteAll={() => {
            setAsking(false);
            actions.deleteSection();
          }}
          onCancel={() => {
            setAsking(false);
            deleteRef.current?.focus();
          }}
        />
      )}
      <div {...fold.panel} className="mt-2 space-y-2">
        <Body {...props} laneRef={drag?.laneRef} cardRef={cardRef} />
      </div>
    </Panel>
  );
}

function DraggableCard(props: CardProps) {
  const id = props.section.section_id;
  const row = useSortableItem({
    id,
    index: props.index,
    lane: SECTIONS,
    type: "section",
    label: `Reorder ${props.section.title}`,
    size: "lg",
  });
  const target = useTarget({ id: intoSection(id), accept: "unplaced" });
  const lane = useLane({
    id: laneOf(id),
    accept: "subsection",
    // The list sits inside the section's own card, which would otherwise win every drop.
    collisionPriority: CollisionPriority.High,
    collisionDetector: pointerIntersection,
  });
  return (
    <Card
      {...props}
      drag={{
        ref: mergeRefs(row.ref, target.ref),
        handle: row.handle,
        chrome: cn(row.chrome, target.chrome),
        laneRef: lane.ref,
      }}
    />
  );
}

/**
 * One section of the plan: its handle, number and title edited in place, its subsections in
 * order, the field that adds a line, and the act that deletes it. A section holding subsections
 * asks where they go before it is deleted. It takes a row dragged into it, and an unplaced figure
 * or claim carried onto it. Read only, it is drawn without any of that. Folded, it says what it
 * holds.
 */
export function SectionCard(props: CardProps) {
  return props.readOnly ? <Card {...props} /> : <DraggableCard {...props} />;
}
